// @flow
/** @jsx isolateComponent */

import { Fragment } from 'react';

import { AsyncIndicator } from '@zapier/common-components';
import { DEFAULT_LOGO_URL } from '@zapier/team/src/constants';
import TeamLogo from '@zapier/team/src/components/TeamLogo';
import { Border, Colors } from '@zapier/theme';
import {
  createKeyHandler,
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

type Props = {
  /** Team id */
  id: number,
  /** True to show without a border */
  isBare?: boolean,
  /** Allows user to edit logo */
  isEditable: boolean,
  /** Shows loading indicator */
  isUploading: boolean,
  /** Maximum file size allowed to be uploaded */
  maxSize: number,
  /** Logo alt text (team name) */
  name: string,
  /** Called when file is changed */
  onChange: (accountId: number, file: File) => void,
  /** Called when clicked */
  onClick: () => void,
  /** Called when user selects an invalid file */
  onInvalid: (message: string) => void,
  /** Called when file uploaded is started */
  onSelectUpload: () => void,
  /** True to upload file when input is changed */
  shouldUploadOnChange: boolean,
  /** Url to current team logo */
  url: string,
};

const Styles = labelStyles(__filename, {
  root: (props, canChange) => [
    {
      alignItems: 'center',
      border: `${Border.size.default} solid ${Border.color.default}`,
      borderRadius: Border.radius.default,
      display: 'flex',
      height: 50,
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative',
      width: 50,
    },
    props.isBare &&
      withModifier('isBare', {
        border: 'none',
        borderRadius: 0,
        height: '100%',
        width: '100%',
      }),
    canChange &&
      withModifier('canChange', {
        cursor: 'pointer',
      }),
  ],
  file: {
    display: 'none',
  },
  icon: {
    fontSize: '1.8em',
  },
  image: {
    height: '100%',
    width: '100%',
    img: {
      height: '100%',
      width: '100%',
    },
  },
  text: permanent => [
    {
      alignItems: 'center',
      background: Colors.white,
      bottom: 0,
      color: Colors.steel,
      display: 'flex',
      fontSize: '0.8em',
      fontWeight: 600,
      left: 0,
      letterSpacing: '0.04em',
      lineHeight: 1.3,
      opacity: 0,
      position: 'absolute',
      right: 0,
      textAlign: 'center',
      textTransform: 'uppercase',
      top: 0,
      transition: 'opacity 0.2s ease-in-out',

      ':hover': {
        opacity: 1,
      },
    },
    permanent &&
      withModifier('permanent', {
        opacity: 1,
      }),
  ],
});

const TeamLogoEditor = (props: Props) => {
  const handleOnChange = e => {
    const file = e.target.files[0];
    const isImage = /^image\//.test((file.type || '').trim());
    if (!isImage) {
      props.onInvalid(`Please choose an image file like a png, gif, or jpeg.`);
    } else if (file.size > props.maxSize) {
      const kb = Math.floor(props.maxSize / 1024);
      props.onInvalid(`Please choose a file smaller than ${kb}KB.`);
    } else {
      props.onChange(props.id, file);
      props.onSelectUpload();
    }
  };

  const canChange = props.isEditable && !props.isUploading;
  const hasDefaultImage =
    typeof props.url === 'string'
      ? props.url.endsWith(DEFAULT_LOGO_URL)
      : false;

  // Show spinner when actively uploading
  const image = props.isUploading ? (
    <AsyncIndicator />
  ) : (
    <div css={Styles.image}>
      <TeamLogo name={props.name} url={props.url} />
    </div>
  );

  const label = !canChange ? null : (
    <span
      css={Styles.text(hasDefaultImage)}
      onClick={props.onClick}
      onKeyDown={createKeyHandler(['enter'], props.onClick)}
      role="button"
      tabIndex={0}
    >
      {hasDefaultImage ? 'Upload' : 'Edit'} Image
    </span>
  );
  const rootStyle = Styles.root(props, canChange);

  const imageAndLabel = (
    <Fragment>
      {image}
      {label}
    </Fragment>
  );

  const renderFileUpload = () => (
    <label css={rootStyle}>
      {imageAndLabel}
      <input
        accept="image/*"
        css={Styles.file}
        disabled={!canChange}
        name="logo"
        onChange={handleOnChange}
        type="file"
        value={'' /* Empty to allow reuploading same file */}
      />
    </label>
  );

  return (
    <div css={props.shouldUploadOnChange ? null : rootStyle}>
      {props.shouldUploadOnChange ? renderFileUpload() : imageAndLabel}
    </div>
  );
};

TeamLogoEditor.defaultProps = {
  maxSize: 1024 * 1024 * 2, // 2 MB in bytes
  onChange: () => {},
  onClick: () => {},
  onInvalid: () => {},
  onSelectUpload: () => {},
  shouldUploadOnChange: true,
};

export default TeamLogoEditor;
